/**
 * Counsellor recommendations — typed by an admin after the learner call,
 * read-only for the learner/guardian. Stored apart from report versions so
 * saved history stays untouched. Prints as its own final page.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

export type College = { name: string; course: string; why: string; exam: string; deadline: string };
type Rec = { summary: string; colleges: College[]; next_steps: string; counsellor_name: string | null; updated_at: string | null };

const emptyCollege = (): College => ({ name: "", course: "", why: "", exam: "", deadline: "" });
const db = supabase as any;

export default function CounsellorRecommendations({ learnerId, editable, adminId }: { learnerId: string; editable?: boolean; adminId?: string }) {
  const [rec, setRec] = useState<Rec | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let off = false;
    void db.from("report_recommendations").select("summary, colleges, next_steps, counsellor_name, updated_at")
      .eq("learner_id", learnerId).maybeSingle().then(({ data }: any) => {
        if (off) return;
        setRec(data ? { ...data, colleges: Array.isArray(data.colleges) ? data.colleges : [] }
          : editable ? { summary: "", colleges: [], next_steps: "", counsellor_name: null, updated_at: null } : null);
        setLoaded(true);
      });
    return () => { off = true; };
  }, [learnerId, editable]);

  if (!loaded || !rec) return null;
  const hasContent = rec.summary.trim() || rec.next_steps.trim() || rec.colleges.some(c => c.name.trim());
  if (!editable && !hasContent) return null;

  const save = async () => {
    setSaving(true); setStatus(null);
    const now = new Date().toISOString();
    const colleges = rec.colleges.filter(c => c.name.trim());
    const { error } = await db.from("report_recommendations").upsert({
      learner_id: learnerId, summary: rec.summary, colleges, next_steps: rec.next_steps,
      counsellor_name: rec.counsellor_name, updated_by: adminId ?? null, updated_at: now,
    }, { onConflict: "learner_id" });
    setSaving(false);
    if (error) { setStatus("Couldn't save. Please try again."); return; }
    setRec({ ...rec, colleges, updated_at: now });
    setStatus("Saved");
    if (adminId) void supabase.from("report_access_log").insert({ admin_id: adminId, learner_id: learnerId, action: "recommendations_saved" } as any);
  };

  const setCollege = (i: number, k: keyof College, v: string) =>
    setRec({ ...rec, colleges: rec.colleges.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });

  return (
    <section aria-labelledby="counsellor-rec" className="dossier-sheet mt-10 rounded-3xl border border-border/60 bg-card p-6 print:break-before-page">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">Counsellor recommendations</p>
      <h2 id="counsellor-rec" className="mt-1 font-display text-2xl font-bold">Suggested next steps from your counsellor</h2>
      {rec.updated_at && (
        <p className="mt-1 text-sm text-muted-foreground">
          {rec.counsellor_name ? `${rec.counsellor_name} · ` : ""}Updated {new Date(rec.updated_at).toLocaleDateString()}
        </p>
      )}

      {editable ? (
        <div className="mt-5 space-y-5 print:hidden">
          <label className="block text-sm font-medium">Your name
            <Input className="mt-1 h-12 rounded-2xl" value={rec.counsellor_name ?? ""} onChange={e => setRec({ ...rec, counsellor_name: e.target.value })} />
          </label>
          <label className="block text-sm font-medium">Summary
            <Textarea className="mt-1 min-h-28 rounded-2xl" value={rec.summary} onChange={e => setRec({ ...rec, summary: e.target.value })} placeholder="What stood out in your conversation" />
          </label>
          <div className="space-y-3">
            <p className="text-sm font-medium">Suggested colleges and courses</p>
            {rec.colleges.map((c, i) => (
              <div key={i} className="grid gap-2 rounded-2xl border border-border/60 p-3 sm:grid-cols-2">
                <Input aria-label="College" placeholder="College" value={c.name} onChange={e => setCollege(i, "name", e.target.value)} />
                <Input aria-label="Course" placeholder="Course" value={c.course} onChange={e => setCollege(i, "course", e.target.value)} />
                <Input aria-label="Entrance exam" placeholder="Entrance exam" value={c.exam} onChange={e => setCollege(i, "exam", e.target.value)} />
                <Input aria-label="Deadline" placeholder="Deadline" value={c.deadline} onChange={e => setCollege(i, "deadline", e.target.value)} />
                <Input aria-label="Why it fits" placeholder="Why it fits" className="sm:col-span-2" value={c.why} onChange={e => setCollege(i, "why", e.target.value)} />
                <Button variant="ghost" className="h-11 justify-self-start" onClick={() => setRec({ ...rec, colleges: rec.colleges.filter((_, j) => j !== i) })}>
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />Remove
                </Button>
              </div>
            ))}
            <Button variant="outline" className="h-12 rounded-2xl" onClick={() => setRec({ ...rec, colleges: [...rec.colleges, emptyCollege()] })}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />Add college
            </Button>
          </div>
          <label className="block text-sm font-medium">Next steps
            <Textarea className="mt-1 min-h-28 rounded-2xl" value={rec.next_steps} onChange={e => setRec({ ...rec, next_steps: e.target.value })} />
          </label>
          <div className="flex items-center gap-3">
            <Button className="h-12 rounded-2xl" disabled={saving} onClick={save}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" aria-hidden="true" />}Save recommendations
            </Button>
            {status && <p role="status" className="text-sm text-muted-foreground">{status}</p>}
          </div>
        </div>
      ) : null}

      <div className={editable ? "hidden print:block" : "mt-5 space-y-5"}>
        {rec.summary.trim() && <p className="whitespace-pre-line">{rec.summary}</p>}
        {rec.colleges.some(c => c.name.trim()) && (
          <ul className="space-y-3">
            {rec.colleges.filter(c => c.name.trim()).map((c, i) => (
              <li key={i} className="rounded-2xl border border-border/60 p-4">
                <p className="font-semibold">{c.name}{c.course && ` · ${c.course}`}</p>
                {c.why && <p className="text-sm">{c.why}</p>}
                {(c.exam || c.deadline) && <p className="text-sm text-muted-foreground">{c.exam && `Exam: ${c.exam}`}{c.exam && c.deadline && " · "}{c.deadline && `Deadline: ${c.deadline}`}</p>}
              </li>
            ))}
          </ul>
        )}
        {rec.next_steps.trim() && <div><p className="font-semibold">Next steps</p><p className="whitespace-pre-line">{rec.next_steps}</p></div>}
      </div>
    </section>
  );
}
